import React, { ReactElement, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { CrumbType } from 'b2b-components/theme/components/Breadcrumbs';
import { usePageViewAnalytics } from 'b2b-apps/shop/analytics/hooks';
import { TEALIUM_PAGE_TYPE } from 'b2b-apps/shop/analytics/types/types';
import { useBreadcrumbs } from 'b2b-apps/shop/breadcrumbs/hooks';
import { isServer } from 'b2b-apps/shop/common/utils/env';
import { META_TAGS } from 'b2b-apps/shop/common/utils/seo/metaTags';
import { config } from 'b2b-apps/shop/config';
import { MetaTagsType } from 'b2b-apps/shop/content/components/ModularPage';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { useManufacturerEffect } from 'b2b-apps/shop/manufacturer/hooks';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import { getProductFallbackImgUrl } from 'b2b-apps/shop/routes/utils/url';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const t = useTranslate();
    const { pathname } = useLocation();
    const { manufacturer } = useParams<{ manufacturer: string }>();
    const manufacturerParam = manufacturer || '';
    const manufacturerId = manufacturerParam.substring(
      manufacturerParam.lastIndexOf('-') + 1,
    );
    const manufacturerData = useManufacturerEffect(manufacturerId);
    const urlCreator = useUrlCreator();
    const { isLoaded } = manufacturerData;
    const isManufacturerDataSpecified = !!manufacturerData;
    const shopName = config.shopName;
    const title = manufacturerData?.data?.name || '';
    const isImagePresent = !!manufacturerData?.data?.linkLogo;
    const cdnImageUrl = isImagePresent && manufacturerData?.data?.linkLogo;
    const imageUrl = cdnImageUrl || getProductFallbackImgUrl();
    const ogUrl = isServer
      ? `${config.url.root}${pathname}`
      : window.location.href;

    useBreadcrumbs(
      useMemo(
        (): CrumbType[] => {
          const crumbs: CrumbType[] = [
            {
              title: t('Manufacturer support pages'),
              url: urlCreator(ROUTES.manufacturers),
            },
          ];

          if (manufacturerData.isLoaded && manufacturerData.data) {
            crumbs.push({
              title: `${manufacturerData.data.name} - ${manufacturerData.data.category}`,
            });
          }

          return crumbs;
        },
        [
          manufacturerData.data,
          manufacturerData.isLoaded,
          t,
          urlCreator,
        ],
      ),
    );

    const metaTags = useMemo(
      (): MetaTagsType => {
        if (!isLoaded || !isManufacturerDataSpecified) {
          return [];
        }

        return [
          {
            name: META_TAGS.ogImage,
            content: imageUrl,
          },
          {
            name: META_TAGS.ogTitle,
            content: title,
          },
          {
            name: META_TAGS.ogType,
            content: 'manufacturer',
          },
          {
            name: META_TAGS.ogUrl,
            content: ogUrl,
          },
          {
            name: META_TAGS.ogSitename,
            content: shopName,
          },
          {
            name: META_TAGS.title,
            content: title,
          },
          {
            name: META_TAGS.twitterCard,
            content: 'summary_large_image',
          },
          {
            name: META_TAGS.twitterImage,
            content: imageUrl,
          },
          {
            name: META_TAGS.twitterTitle,
            content: title,
          },
        ];
      },
      [
        imageUrl,
        isLoaded,
        isManufacturerDataSpecified,
        ogUrl,
        shopName,
        title,
      ],
    );

    usePageViewAnalytics({
      pageType: TEALIUM_PAGE_TYPE.brand,
      remoteRecord: manufacturerData,
    });

    const injectedProps: InjectedProps = {
      manufacturer: manufacturerData,
      urlCreator,
      metaTags,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps));
};

export { withContainer };
